///
/// EarthBrowser
///
/// Created by Matt Giger on 9/13/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Persisted anonymous-engagement tracking for the account-conversion
/// funnel. Records how invested a visitor is (sessions = distinct calendar
/// days of use) so conversion prompts can escalate with engagement instead
/// of wall-clock time. Best-effort by design: losing this state (cleared
/// localStorage, new device) just makes the funnel more polite, never less.
///
/// The durable anchor is the server-set eb_anon_id cookie (see
/// api-server/src/user/anonymous-session.ts), fetched once per launch via
/// engagementApi and mirrored here for analytics stitching.
///

import { createSlice, type PayloadAction } from "@reduxjs/toolkit"

export interface EngagementState {
	/** Server-issued anonymous device id (mirrors the eb_anon_id cookie) */
	anonymousId: string | null
	/** ISO timestamp of the first recorded session on this install */
	firstSeenAt: string | null
	/** Number of distinct local calendar days the app has been used */
	sessionCount: number
	/** Local calendar day ("YYYY-MM-DD") of the last recorded session */
	lastSessionDay: string | null
	/** First-ever session arrived via a shared deep link or external referrer */
	firstSessionWasDeepLink: boolean
	/** Most recent session arrived via a shared deep link or external referrer */
	lastSessionWasDeepLink: boolean

	// ========== Conversion-prompt bookkeeping (ladder rungs) ==========

	/** Day the support interstitial last appeared (max once per day) */
	interstitialLastShownDay: string | null
	/** Lifetime interstitial showings; drives the escalating dismiss delay */
	interstitialShowCount: number
	/**
	 * Dismiss-delay seconds of a currently-showing interstitial. Cleared
	 * only on dismissal, so an undismissed interstitial survives page
	 * refreshes (reloading is not an escape hatch).
	 */
	interstitialPendingDelay: number | null
	/** Day the rung-1 conversion banner was dismissed (hides it for the day) */
	bannerDismissedDay: string | null
	/**
	 * One-time saved-view sync prompt has been shown (fires when an
	 * anonymous user saves their second view; never repeats)
	 */
	savedViewPromptShown: boolean
	/**
	 * ISO timestamp until which the signed-in full-access offer card stays
	 * hidden after a "Not now" (eligibility itself is server-side)
	 */
	offerSnoozedUntil: string | null
}

const initialState: EngagementState = {
	anonymousId: null,
	firstSeenAt: null,
	sessionCount: 0,
	lastSessionDay: null,
	firstSessionWasDeepLink: false,
	lastSessionWasDeepLink: false,
	interstitialLastShownDay: null,
	interstitialShowCount: 0,
	interstitialPendingDelay: null,
	bannerDismissedDay: null,
	savedViewPromptShown: false,
	offerSnoozedUntil: null,
}

// "Not now" on the full-access offer hides it for this long
const cOfferSnoozeDays = 14

// Interstitial dismiss delay escalates by this many seconds per showing…
const cDismissDelayStepS = 5
// …capped here
const cMaxDismissDelayS = 20

/** Local (not UTC) calendar day, so "a day of use" matches the user's clock */
export function localDay(date: Date): string {
	const month = String(date.getMonth() + 1).padStart(2, "0")
	const day = String(date.getDate()).padStart(2, "0")
	return `${date.getFullYear()}-${month}-${day}`
}

/**
 * The conversion-ladder rung for the current session. Prompts escalate with
 * engagement (sessions = distinct days of use), never with wall-clock time
 * inside one session:
 *
 * - Rung 0 (first session): no interrupts at all; the sign-in row in the
 *   account menu (header/AccountMenu.tsx) is the only conversion surface.
 * - Rung 1 (sessions 2-3): passive dismissible banner.
 * - Rung 2 (sessions 4+, or 7+ days of tenure): the support interstitial,
 *   at most once per day.
 *
 * Deep-link etiquette: a first session that arrived via a shared link does
 * not count toward escalation (the ladder starts one rung lower), and no
 * session that arrived via a deep link ever shows the interstitial.
 */
export type ConversionRung = 0 | 1 | 2

export function conversionRung(state: EngagementState, now: Date = new Date()): ConversionRung {
	const effectiveSessions = state.sessionCount - (state.firstSessionWasDeepLink ? 1 : 0)
	if (effectiveSessions <= 1) return 0

	const daysSinceFirstSeen = state.firstSeenAt
		? Math.floor((now.getTime() - new Date(state.firstSeenAt).getTime()) / 86_400_000)
		: 0
	const interstitialReady = effectiveSessions >= 4 || daysSinceFirstSeen >= 7

	if (interstitialReady && !state.lastSessionWasDeepLink) return 2
	return 1
}

/**
 * Whether the rung-1 banner is due this session (rung 1 and not yet
 * dismissed today). Callers still gate on signed-out / non-beta
 * themselves.
 */
export function bannerDue(state: EngagementState, now: Date = new Date()): boolean {
	return conversionRung(state, now) === 1 && state.bannerDismissedDay !== localDay(now)
}

// ========== Enforcement wall thresholds ==========
// The highest-stakes numbers in the funnel: deliberately generous, and
// meant to be tuned once auth_dialog_opened / signin_wall_shown funnel
// data has accumulated. Effective sessions exclude a deep-link first visit.
const cWallSessionThreshold = 8
// Tenure alone can also trigger the wall, but only with real repeat usage
// (otherwise someone whose second-ever visit lands three weeks after their
// first would be walled immediately)
const cWallTenureDays = 21
const cWallTenureMinSessions = 4

/**
 * Whether the required-sign-in wall applies to the current session. True
 * after 8 effective sessions, or 21+ days of tenure with 4+ sessions.
 *
 * Deep-link etiquette carries through: any session that arrived via a
 * shared link is exempt, so shared links never dead-end — the wall
 * returns on their next organic visit.
 */
export function signinWallActive(state: EngagementState, now: Date = new Date()): boolean {
	if (state.lastSessionWasDeepLink) return false

	const effectiveSessions = state.sessionCount - (state.firstSessionWasDeepLink ? 1 : 0)
	if (effectiveSessions >= cWallSessionThreshold) return true

	const daysSinceFirstSeen = state.firstSeenAt
		? Math.floor((now.getTime() - new Date(state.firstSeenAt).getTime()) / 86_400_000)
		: 0
	return daysSinceFirstSeen >= cWallTenureDays && effectiveSessions >= cWallTenureMinSessions
}

export const engagementSlice = createSlice({
	name: "engagement",
	initialState,
	reducers: {
		/**
		 * Record an app launch. Increments the session count at most once
		 * per local calendar day, so reloads and multiple tabs in one day
		 * count as a single session.
		 */
		recordSession: {
			reducer: (state, action: PayloadAction<{ now: string; day: string; isDeepLink: boolean }>) => {
				const { now, day, isDeepLink } = action.payload
				if (!state.firstSeenAt) {
					state.firstSeenAt = now
					state.firstSessionWasDeepLink = isDeepLink
				}
				if (state.lastSessionDay !== day) {
					state.sessionCount += 1
					state.lastSessionDay = day
				}
				state.lastSessionWasDeepLink = isDeepLink
			},
			prepare: (isDeepLink: boolean) => {
				const now = new Date()
				return { payload: { now: now.toISOString(), day: localDay(now), isDeepLink } }
			},
		},

		/** Mirror the server-issued eb_anon_id cookie value */
		setAnonymousId: (state, action: PayloadAction<string>) => {
			if (typeof action.payload === "string" && action.payload.length > 0) {
				state.anonymousId = action.payload
			}
		},

		/**
		 * Mark the interstitial as shown today and arm its dismiss delay
		 * (0s on the first-ever showing, +5s per showing, capped at 20s).
		 * The pending delay stays set until dismissal so a refresh restores
		 * the interstitial with the full countdown.
		 */
		recordInterstitialShown: {
			reducer: (state, action: PayloadAction<{ day: string }>) => {
				state.interstitialLastShownDay = action.payload.day
				state.interstitialShowCount += 1
				state.interstitialPendingDelay = Math.min(
					(state.interstitialShowCount - 1) * cDismissDelayStepS,
					cMaxDismissDelayS
				)
			},
			prepare: () => ({ payload: { day: localDay(new Date()) } }),
		},

		/** Interstitial dismissed: disarm the refresh-survival delay */
		dismissInterstitial: (state) => {
			state.interstitialPendingDelay = null
		},

		/** Hide the rung-1 banner for the rest of the day */
		dismissBanner: {
			reducer: (state, action: PayloadAction<{ day: string }>) => {
				state.bannerDismissedDay = action.payload.day
			},
			prepare: () => ({ payload: { day: localDay(new Date()) } }),
		},

		/** The one-time saved-view sync prompt has been shown; never repeat */
		recordSavedViewPromptShown: (state) => {
			state.savedViewPromptShown = true
		},

		/** "Not now" on the full-access offer: hide the card for two weeks */
		snoozeFullAccessOffer: {
			reducer: (state, action: PayloadAction<{ until: string }>) => {
				state.offerSnoozedUntil = action.payload.until
			},
			prepare: () => ({
				payload: { until: new Date(Date.now() + cOfferSnoozeDays * 86_400_000).toISOString() },
			}),
		},

		// Applied by launch validation to repair a corrupted persisted slice
		applyValidatedEngagementState: (_state, action: PayloadAction<EngagementState>) => {
			return action.payload
		},
	},
})

export const {
	recordSession,
	setAnonymousId,
	recordInterstitialShown,
	dismissInterstitial,
	dismissBanner,
	recordSavedViewPromptShown,
	snoozeFullAccessOffer,
	applyValidatedEngagementState,
} = engagementSlice.actions

export default engagementSlice.reducer
